// src/views/Admin/Locales/LocalPage/components/UsersGrid/UsersGrid.tsx

import React, { useState, useMemo } from 'react';
import { 
  HiOutlineUserCircle, 
  HiOutlineEnvelope, 
  HiOutlineShieldCheck,
  HiOutlineChevronDown,
  HiOutlineChevronRight,
  HiOutlineEye,
  HiOutlinePlusCircle,
  HiOutlinePencilSquare,
  HiOutlineTrash,
  HiOutlineCheck,
  HiOutlineXMark,
  HiOutlineBriefcase,
  HiOutlineUsers,
  HiOutlineClipboardDocument,
  HiOutlineLockClosed
} from 'react-icons/hi2';
import { FiSearch, FiKey, FiX } from 'react-icons/fi';
import { FaSignInAlt } from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  getUserPermissions,
  updateUserPermission,
  enablePermissionsByTypes,
  disablePermissionsByTypes
} from '../../../../../../crud/users_api/user_permissions';
import { deletePuestoById, updatePuesto } from '../../../../../../crud/management_api/puestos';
import MiniLoadingSpinner from '../../../../../../components/Spinners/MiniLoadingSpinner';
import './UsersGrid.scss';

// Permission types
interface Permission {
  name: string;
  permission_id: number;
  description: string;
  category: string;
  subcategory: string | null;
  permission_type: string | null;
  enabled: boolean;
}

interface GroupedPermissions {
  [category: string]: {
    [subcategory: string]: Permission[];
  };
}

interface User {
  user_id: number;
  name: string;
  email: string;
  state: boolean | number;
  is_admin?: boolean;
  rol_name?: string;
  puesto_name?: string;
  password?: string;
}

interface Puesto {
  puesto_id: number;
  puesto_name: string;
  local_id: number;
}

interface UsersGridProps {
  users: User[];
  title?: string;
  onCreateClick?: () => void;
  // Puestos props
  puestos?: Puesto[];
  puestosLoading?: boolean;
  onCreatePuestoClick?: () => void;
  onPuestosChange?: () => void;
}

// Permission type configuration - using orange palette
const PERMISSION_TYPES = [
  { value: 'read', label: 'Leer', icon: HiOutlineEye, color: '#FC7556' },
  { value: 'create', label: 'Crear', icon: HiOutlinePlusCircle, color: '#e85d3a' },
  { value: 'update', label: 'Actualizar', icon: HiOutlinePencilSquare, color: '#d14a28' },
  { value: 'delete', label: 'Eliminar', icon: HiOutlineTrash, color: '#b83d1e' }
];

const UsersGrid: React.FC<UsersGridProps> = ({ 
  users, 
  title = 'Usuarios del Local', 
  onCreateClick,
  puestos = [],
  puestosLoading = false,
  onCreatePuestoClick,
  onPuestosChange
}) => {
  const [usersGridCollapsed, setUsersGridCollapsed] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');
  const [activeView, setActiveView] = useState<'users' | 'puestos'>('users');
  
  // Permission management state
  const [expandedUserId, setExpandedUserId] = useState<number | null>(null);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [loadingPermissions, setLoadingPermissions] = useState(false);
  const [permissionSearch, setPermissionSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
  const [expandedSubcategories, setExpandedSubcategories] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState<string | null>(null);

  // Puestos management state
  const [puestoSearchTerm, setPuestoSearchTerm] = useState('');
  const [editingPuestoId, setEditingPuestoId] = useState<number | null>(null);
  const [editingPuestoName, setEditingPuestoName] = useState('');
  const [deletingPuestoId, setDeletingPuestoId] = useState<number | null>(null);
  const [savingPuestoId, setSavingPuestoId] = useState<number | null>(null);

  const isUserActive = (user: User) => user.state === true || user.state === 1;

  const filteredUsers = users.filter(user => {
    const matchesSearch = 
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (user.rol_name && user.rol_name.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (user.puesto_name && user.puesto_name.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesFilter = 
      filterActive === 'all' ||
      (filterActive === 'active' && isUserActive(user)) ||
      (filterActive === 'inactive' && !isUserActive(user));

    return matchesSearch && matchesFilter;
  });

  const activeCount = users.filter(isUserActive).length;
  const inactiveCount = users.length - activeCount;

  // Filtered puestos
  const filteredPuestos = puestos.filter(p =>
    p.puesto_name.toLowerCase().includes(puestoSearchTerm.toLowerCase())
  );

  // Puesto handlers
  const handleStartEditPuesto = (puesto: Puesto) => {
    setEditingPuestoId(puesto.puesto_id);
    setEditingPuestoName(puesto.puesto_name);
  };

  const handleCancelEditPuesto = () => {
    setEditingPuestoId(null);
    setEditingPuestoName('');
  };

  const handleSaveEditPuesto = async (puestoId: number) => {
    if (!editingPuestoName.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }

    setSavingPuestoId(puestoId);
    try {
      await updatePuesto(puestoId, { puesto_name: editingPuestoName.trim() });
      toast.success('Puesto actualizado');
      setEditingPuestoId(null);
      setEditingPuestoName('');
      onPuestosChange?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al actualizar puesto');
    } finally {
      setSavingPuestoId(null);
    }
  };

  const handleDeletePuesto = async (puestoId: number) => {
    if (!window.confirm('¿Estás seguro de eliminar este puesto?')) {
      return;
    }

    setDeletingPuestoId(puestoId);
    try {
      await deletePuestoById(puestoId);
      toast.success('Puesto eliminado');
      onPuestosChange?.();
    } catch (error: any) {
      toast.error(error?.response?.data?.message || 'Error al eliminar puesto');
    } finally {
      setDeletingPuestoId(null);
    }
  };

  const handlePuestoKeyDown = (e: React.KeyboardEvent, puestoId: number) => {
    if (e.key === 'Enter') {
      handleSaveEditPuesto(puestoId);
    }
    if (e.key === 'Escape') {
      handleCancelEditPuesto();
    }
  };

  // Load permissions for a user
  const loadUserPermissions = async (userId: number) => {
    setLoadingPermissions(true);
    try {
      const response = await getUserPermissions(userId);
      const perms: Permission[] = Object.values(response.data);
      setPermissions(perms);
      
      // Expand all categories by default
      const categories = new Set<string>(perms.map(p => p.category));
      setExpandedCategories(categories);
      
      // Also expand all subcategories by default for better UX
      const subcats = new Set<string>();
      perms.forEach(p => {
        const category = p.category || 'Sin categoría';
        const subcategory = p.subcategory || 'General';
        subcats.add(`${category}-${subcategory}`);
      });
      setExpandedSubcategories(subcats);
    } catch (error) {
      console.error(error);
      toast.error('Error al cargar permisos');
    } finally {
      setLoadingPermissions(false);
    }
  };

  // Toggle permission panel for a user
  const togglePermissions = (userId: number) => {
    if (expandedUserId === userId) {
      setExpandedUserId(null);
      setPermissions([]);
      setPermissionSearch('');
      setTypeFilter('all');
    } else {
      setExpandedUserId(userId);
      setPermissionSearch('');
      setTypeFilter('all');
      loadUserPermissions(userId);
    }
  };

  // Group and filter permissions
  const groupedPermissions = useMemo(() => {
    let filtered = permissions.filter(p =>
      p.name.toLowerCase().includes(permissionSearch.toLowerCase()) ||
      (p.description?.toLowerCase().includes(permissionSearch.toLowerCase())) ||
      p.category.toLowerCase().includes(permissionSearch.toLowerCase()) ||
      (p.subcategory?.toLowerCase().includes(permissionSearch.toLowerCase()))
    );

    // Apply type filter
    if (typeFilter !== 'all') {
      if (typeFilter === 'none') {
        filtered = filtered.filter(p => !p.permission_type);
      } else {
        filtered = filtered.filter(p => p.permission_type === typeFilter);
      }
    }

    // Group by category, then subcategory
    const grouped: GroupedPermissions = {};
    
    filtered.forEach(permission => {
      const category = permission.category || 'Sin categoría';
      const subcategory = permission.subcategory || 'General';
      
      if (!grouped[category]) {
        grouped[category] = {};
      }
      if (!grouped[category][subcategory]) {
        grouped[category][subcategory] = [];
      }
      grouped[category][subcategory].push(permission);
    });

    // Sort subcategories and permissions within each
    Object.keys(grouped).forEach(category => {
      const sortedSubcats: { [key: string]: Permission[] } = {};
      Object.keys(grouped[category]).sort().forEach(sub => {
        sortedSubcats[sub] = grouped[category][sub].sort((a, b) => 
          a.name.localeCompare(b.name)
        );
      });
      grouped[category] = sortedSubcats;
    });

    return grouped;
  }, [permissions, permissionSearch, typeFilter]);

  // Toggle permission
  const handleTogglePermission = async (permissionId: number, enabled: boolean) => {
    if (!expandedUserId) return;

    // Optimistic update
    setPermissions(prev =>
      prev.map(p =>
        p.permission_id === permissionId ? { ...p, enabled } : p
      )
    );

    try {
      await updateUserPermission(expandedUserId, permissionId, enabled);
      toast.success('Permiso actualizado');
    } catch (error) {
      // Revert on error
      setPermissions(prev =>
        prev.map(p =>
          p.permission_id === permissionId ? { ...p, enabled: !enabled } : p
        )
      );
      toast.error('Error al actualizar permiso');
    }
  };

  // Bulk enable permissions by type
  const handleBulkEnable = async (type: string) => {
    if (!expandedUserId) return;
    
    setBulkLoading(`enable-${type}`);
    try {
      await enablePermissionsByTypes(expandedUserId, [type]);
      await loadUserPermissions(expandedUserId);
      const typeLabel = PERMISSION_TYPES.find(t => t.value === type)?.label || type;
      toast.success(`Permisos de "${typeLabel}" habilitados`);
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Bulk disable permissions by type
  const handleBulkDisable = async (type: string) => {
    if (!expandedUserId) return;
    
    setBulkLoading(`disable-${type}`);
    try {
      await disablePermissionsByTypes(expandedUserId, [type]);
      await loadUserPermissions(expandedUserId);
      const typeLabel = PERMISSION_TYPES.find(t => t.value === type)?.label || type;
      toast.success(`Permisos de "${typeLabel}" deshabilitados`);
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Enable all permissions
  const handleEnableAll = async () => {
    if (!expandedUserId) return;
    
    setBulkLoading('enable-all');
    try {
      await enablePermissionsByTypes(expandedUserId, ['read', 'create', 'update', 'delete']);
      await loadUserPermissions(expandedUserId);
      toast.success('Todos los permisos habilitados');
    } catch (error) {
      toast.error('Error al habilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Disable all permissions
  const handleDisableAll = async () => {
    if (!expandedUserId) return;
    
    setBulkLoading('disable-all');
    try {
      await disablePermissionsByTypes(expandedUserId, ['read', 'create', 'update', 'delete']);
      await loadUserPermissions(expandedUserId);
      toast.success('Todos los permisos deshabilitados');
    } catch (error) {
      toast.error('Error al deshabilitar permisos');
    } finally {
      setBulkLoading(null);
    }
  };

  // Toggle category expansion
  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) {
        next.delete(category);
      } else {
        next.add(category);
      }
      return next;
    });
  };

  // Toggle subcategory expansion
  const toggleSubcategory = (key: string) => {
    setExpandedSubcategories(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Get category permission count
  const getCategoryCount = (category: string) => {
    return Object.values(groupedPermissions[category] || {}).reduce(
      (sum, perms) => sum + perms.length, 0
    );
  };

  // Get enabled count for category
  const getCategoryEnabledCount = (category: string) => {
    return Object.values(groupedPermissions[category] || {}).reduce(
      (sum, perms) => sum + perms.filter(p => p.enabled).length, 0
    );
  };

  // Get permission type badge
  const getTypeBadge = (type: string | null) => {
    if (!type) return null;
    const config = PERMISSION_TYPES.find(t => t.value === type);
    if (!config) return null;
    const Icon = config.icon;
    return (
      <span className={`type-badge type-${type}`} style={{ '--type-color': config.color } as React.CSSProperties}>
        <Icon size={12} />
        <span>{config.label}</span>
      </span>
    );
  };

  // Open login page with credentials in URL
  const handleLoginAs = (user: User) => {
    if (!user.password) {
      toast.error('No tienes acceso a las credenciales de este usuario');
      return;
    }

    // Encode credentials for URL
    const params = new URLSearchParams({
      email: user.email,
      pwd: user.password
    });
    
    const loginUrl = `${window.location.origin}/login?${params.toString()}`;
    
    // Open in new tab
    window.open(loginUrl, '_blank');
  };

  // Copy to clipboard handler
  const handleCopyToClipboard = async (text: string, label: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado al portapapeles`);
    } catch (err) {
      toast.error('Error al copiar');
    }
  };

  return (
    <div className="users-grid-card">
      <div className="users-grid-header">
        <div className="header-title">
          <button className="usersgrid-collapse-toggle" onClick={() => setUsersGridCollapsed(!usersGridCollapsed)}>
            {usersGridCollapsed ? <HiOutlineChevronRight size={20} /> : <HiOutlineChevronDown size={20} />}
          </button>
          <h3>{title}</h3>
          <div className="user-counts">
            <span className="count-badge active">{activeCount} activos</span>
            <span className="count-badge inactive">{inactiveCount} inactivos</span>
          </div>
        </div>
        
        <div className="header-controls">
          {/* View Toggle - Users / Puestos */}
          <div className="view-toggle">
            <button 
              className={`view-btn ${activeView === 'users' ? 'active' : ''}`}
              onClick={() => setActiveView('users')}
            >
              <HiOutlineUsers size={16} />
              <span>Usuarios</span>
            </button>
            <button 
              className={`view-btn ${activeView === 'puestos' ? 'active' : ''}`}
              onClick={() => setActiveView('puestos')}
            >
              <HiOutlineBriefcase size={16} />
              <span>Puestos</span>
              <span className="count">{puestos.length}</span>
            </button>
          </div>

          {activeView === 'users' ? (
            <>
              <div className="search-input">
                <FiSearch size={16} />
                <input
                  type="text"
                  placeholder="Buscar usuario, rol o puesto..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
              
              <div className="filter-buttons">
                <button 
                  className={filterActive === 'all' ? 'active' : ''}
                  onClick={() => setFilterActive('all')}
                >
                  Todos
                </button>
                <button 
                  className={filterActive === 'active' ? 'active' : ''}
                  onClick={() => setFilterActive('active')}
                >
                  Activos
                </button>
                <button 
                  className={filterActive === 'inactive' ? 'active' : ''}
                  onClick={() => setFilterActive('inactive')}
                >
                  Inactivos
                </button>
              </div>

              {onCreateClick && (
                <button className="create-user-btn" onClick={onCreateClick}>
                  <HiOutlinePlusCircle size={16} />
                  <span>Nuevo Usuario</span>
                </button>
              )}
            </>
          ) : (
            <>
              <div className="search-input">
                <FiSearch size={16} />
                <input
                  type="text"
                  placeholder="Buscar puesto..."
                  value={puestoSearchTerm}
                  onChange={(e) => setPuestoSearchTerm(e.target.value)}
                />
              </div>

              {onCreatePuestoClick && (
                <button className="create-user-btn" onClick={onCreatePuestoClick}>
                  <HiOutlinePlusCircle size={16} />
                  <span>Nuevo Puesto</span>
                </button>
              )}
            </>
          )}
        </div>
      </div>

      {!usersGridCollapsed && (
      <div className="users-grid-body">
        {/* Users View */}
        {activeView === 'users' && (
          <>
            {filteredUsers.length === 0 ? (
              <div className="no-users">
                <HiOutlineUserCircle size={48} />
                <p>No se encontraron usuarios</p>
              </div>
            ) : (
              <div className="users-list">
            {filteredUsers.map((user) => (
              <div 
                key={user.user_id} 
                className={`user-item-wrapper ${expandedUserId === user.user_id ? 'expanded' : ''}`}
              >
                <div className={`user-item ${isUserActive(user) ? 'active' : 'inactive'}`}>
                  <div className="user-avatar">
                    <HiOutlineUserCircle size={40} />
                    {user.is_admin && (
                      <span className="admin-badge" title="Administrador">
                        <HiOutlineShieldCheck size={12} />
                      </span>
                    )}
                  </div>
                  <div className="user-info">
                    <span className="user-name">{user.name}</span>
                    <div className="user-credential">
                      <HiOutlineEnvelope size={12} />
                      <span className="credential-value">{user.email}</span>
                      <button 
                        className="copy-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCopyToClipboard(user.email, 'Email');
                        }}
                        title="Copiar email"
                      >
                        <HiOutlineClipboardDocument size={14} />
                      </button>
                    </div>
                    {user.password && (
                      <div className="user-credential password">
                        <HiOutlineLockClosed size={12} />
                        <span className="credential-value password-masked">••••••••</span>
                        <button 
                          className="copy-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleCopyToClipboard(user.password!, 'Contraseña');
                          }}
                          title="Copiar contraseña"
                        >
                          <HiOutlineClipboardDocument size={14} />
                        </button>
                      </div>
                    )}
                    <div className="user-meta">
                      {user.rol_name && (
                        <span className="user-role">{user.rol_name}</span>
                      )}
                      {user.puesto_name && (
                        <span className="user-puesto">{user.puesto_name}</span>
                      )}
                    </div>
                  </div>
                  <div className={`user-status ${isUserActive(user) ? 'active' : 'inactive'}`}>
                    <span className="status-dot" />
                    {isUserActive(user) ? 'Activo' : 'Inactivo'}
                  </div>
                  <div className="user-actions">
                    <button 
                      className={`permissions-button ${expandedUserId === user.user_id ? 'active' : ''}`}
                      onClick={() => togglePermissions(user.user_id)}
                      title="Gestionar permisos"
                    >
                      <FiKey size={14} />
                      <span>Permisos</span>
                    </button>
                    {user.password && (
                      <button 
                        className="login-as-button"
                        onClick={() => handleLoginAs(user)}
                        title="Iniciar sesión como este usuario"
                      >
                        <FaSignInAlt size={14} />
                        <span>Loguearse</span>
                      </button>
                    )}
                  </div>
                </div>

                {/* Permission Panel */}
                {expandedUserId === user.user_id && (
                  <div className="permissions-panel">
                    <div className="permissions-panel-header">
                      <h4>
                        <FiKey size={16} />
                        Permisos de {user.name}
                      </h4>
                      <button 
                        className="close-panel-btn"
                        onClick={() => togglePermissions(user.user_id)}
                      >
                        <FiX size={18} />
                      </button>
                    </div>

                    {loadingPermissions ? (
                      <div className="permissions-loading">
                        <MiniLoadingSpinner />
                        <p>Cargando permisos...</p>
                      </div>
                    ) : (
                      <>
                        {/* Bulk Actions */}
                        <div className="bulk-actions">
                          <div className="bulk-section">
                            <span className="bulk-label">Acciones por tipo:</span>
                            <div className="type-actions">
                              {PERMISSION_TYPES.map(type => {
                                const Icon = type.icon;
                                return (
                                  <div key={type.value} className="type-action-group">
                                    <span className="type-name" style={{ color: type.color }}>
                                      <Icon size={14} />
                                      {type.label}
                                    </span>
                                    <button
                                      className="bulk-btn enable"
                                      onClick={() => handleBulkEnable(type.value)}
                                      disabled={bulkLoading !== null}
                                      title={`Habilitar todos los permisos de ${type.label}`}
                                    >
                                      {bulkLoading === `enable-${type.value}` ? (
                                        <MiniLoadingSpinner />
                                      ) : (
                                        <HiOutlineCheck size={12} />
                                      )}
                                    </button>
                                    <button
                                      className="bulk-btn disable"
                                      onClick={() => handleBulkDisable(type.value)}
                                      disabled={bulkLoading !== null}
                                      title={`Deshabilitar todos los permisos de ${type.label}`}
                                    >
                                      {bulkLoading === `disable-${type.value}` ? (
                                        <MiniLoadingSpinner />
                                      ) : (
                                        <HiOutlineXMark size={12} />
                                      )}
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                          <div className="bulk-all">
                            <button
                              className="all-btn enable"
                              onClick={handleEnableAll}
                              disabled={bulkLoading !== null}
                            >
                              {bulkLoading === 'enable-all' ? <MiniLoadingSpinner /> : <HiOutlineCheck size={14} />}
                              Habilitar Todos
                            </button>
                            <button
                              className="all-btn disable"
                              onClick={handleDisableAll}
                              disabled={bulkLoading !== null}
                            >
                              {bulkLoading === 'disable-all' ? <MiniLoadingSpinner /> : <HiOutlineXMark size={14} />}
                              Deshabilitar Todos
                            </button>
                          </div>
                        </div>

                        {/* Search & Filters */}
                        <div className="permissions-filters">
                          <div className="permissions-search">
                            <FiSearch size={16} />
                            <input
                              type="text"
                              placeholder="Buscar permisos..."
                              value={permissionSearch}
                              onChange={(e) => setPermissionSearch(e.target.value)}
                            />
                          </div>
                          <div className="type-filter">
                            <select
                              value={typeFilter}
                              onChange={(e) => setTypeFilter(e.target.value)}
                              className={typeFilter !== 'all' ? 'active' : ''}
                            >
                              <option value="all">Todos los tipos</option>
                              <option value="none">Sin tipo</option>
                              {PERMISSION_TYPES.map(type => (
                                <option key={type.value} value={type.value}>{type.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* Permissions List */}
                        {Object.keys(groupedPermissions).length > 0 ? (
                          <div className="permissions-list">
                            {Object.entries(groupedPermissions).map(([category, subcats]) => {
                              const isExpanded = expandedCategories.has(category);
                              const totalCount = getCategoryCount(category);
                              const enabledCount = getCategoryEnabledCount(category);
                              
                              return (
                                <div key={category} className="category-group">
                                  <div
                                    className="category-header"
                                    onClick={() => toggleCategory(category)}
                                  >
                                    {isExpanded ? (
                                      <HiOutlineChevronDown size={18} />
                                    ) : (
                                      <HiOutlineChevronRight size={18} />
                                    )}
                                    <span className="category-name">{category}</span>
                                    <span className="category-stats">
                                      <span className="enabled-count">{enabledCount}</span>
                                      <span className="separator">/</span>
                                      <span className="total-count">{totalCount}</span>
                                    </span>
                                  </div>

                                  {isExpanded && (
                                    <div className="category-content">
                                      {Object.entries(subcats).map(([subcategory, perms]) => {
                                        const subKey = `${category}-${subcategory}`;
                                        const isSubExpanded = expandedSubcategories.has(subKey);
                                        const subEnabledCount = perms.filter(p => p.enabled).length;
                                        
                                        return (
                                          <div key={subKey} className="subcategory-group">
                                            <div
                                              className="subcategory-header"
                                              onClick={() => toggleSubcategory(subKey)}
                                            >
                                              {isSubExpanded ? (
                                                <HiOutlineChevronDown size={14} />
                                              ) : (
                                                <HiOutlineChevronRight size={14} />
                                              )}
                                              <span className="subcategory-name">{subcategory}</span>
                                              <span className="subcategory-stats">
                                                {subEnabledCount}/{perms.length}
                                              </span>
                                            </div>

                                            {isSubExpanded && (
                                              <div className="permissions-items">
                                                {perms.map(permission => (
                                                  <div
                                                    key={permission.permission_id}
                                                    className={`permission-item ${permission.enabled ? 'enabled' : 'disabled'}`}
                                                  >
                                                    <div className="permission-info">
                                                      <div className="permission-header">
                                                        {getTypeBadge(permission.permission_type)}
                                                      </div>
                                                      <span className="permission-name">{permission.name}</span>
                                                      {permission.description && (
                                                        <span className="permission-description">{permission.description}</span>
                                                      )}
                                                    </div>
                                                    <label className="toggle-switch">
                                                      <input
                                                        type="checkbox"
                                                        checked={permission.enabled}
                                                        onChange={() => handleTogglePermission(
                                                          permission.permission_id,
                                                          !permission.enabled
                                                        )}
                                                      />
                                                      <span className="slider"></span>
                                                    </label>
                                                  </div>
                                                ))}
                                              </div>
                                            )}
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <div className="no-permissions">
                            <HiOutlineShieldCheck size={32} />
                            <p>No se encontraron permisos</p>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            ))}
              </div>
            )}
          </>
        )}

        {/* Puestos View */}
        {activeView === 'puestos' && (
          <div className="puestos-subview">
            {puestosLoading ? (
              <div className="puestos-loading">
                <MiniLoadingSpinner />
                <p>Cargando puestos...</p>
              </div>
            ) : puestos.length === 0 ? (
              <div className="no-puestos">
                <HiOutlineBriefcase size={48} />
                <p>No hay puestos de trabajo</p>
                <span>Crea un puesto para poder asignar usuarios</span>
                {onCreatePuestoClick && (
                  <button className="create-first-btn" onClick={onCreatePuestoClick}>
                    <HiOutlinePlusCircle size={18} />
                    Crear Primer Puesto
                  </button>
                )}
              </div>
            ) : filteredPuestos.length === 0 ? (
              <div className="no-puestos">
                <FiSearch size={48} />
                <p>No se encontraron puestos</p>
                <span>Intenta con otro término de búsqueda</span>
              </div>
            ) : (
              <div className="puestos-list">
                {filteredPuestos.map((puesto) => (
                  <div key={puesto.puesto_id} className="puesto-item">
                    <div className="puesto-icon">
                      <HiOutlineBriefcase size={20} />
                    </div>
                    
                    {editingPuestoId === puesto.puesto_id ? (
                      <div className="puesto-edit">
                        <input
                          type="text"
                          value={editingPuestoName}
                          onChange={(e) => setEditingPuestoName(e.target.value)}
                          onKeyDown={(e) => handlePuestoKeyDown(e, puesto.puesto_id)}
                          autoFocus
                          disabled={savingPuestoId === puesto.puesto_id}
                        />
                        <div className="edit-actions">
                          <button 
                            className="save-btn"
                            onClick={() => handleSaveEditPuesto(puesto.puesto_id)}
                            disabled={savingPuestoId === puesto.puesto_id}
                          >
                            {savingPuestoId === puesto.puesto_id ? (
                              <MiniLoadingSpinner />
                            ) : (
                              'Guardar'
                            )}
                          </button>
                          <button 
                            className="cancel-btn"
                            onClick={handleCancelEditPuesto}
                            disabled={savingPuestoId === puesto.puesto_id}
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    ) : (
                      <>
                        <div className="puesto-info">
                          <span className="puesto-name">{puesto.puesto_name}</span>
                          <span className="puesto-id">ID: {puesto.puesto_id}</span>
                        </div>
                        
                        <div className="puesto-actions">
                          <button 
                            className="action-btn edit"
                            onClick={() => handleStartEditPuesto(puesto)}
                            title="Editar puesto"
                          >
                            <HiOutlinePencilSquare size={16} />
                          </button>
                          <button 
                            className="action-btn delete"
                            onClick={() => handleDeletePuesto(puesto.puesto_id)}
                            disabled={deletingPuestoId === puesto.puesto_id}
                            title="Eliminar puesto"
                          >
                            {deletingPuestoId === puesto.puesto_id ? (
                              <MiniLoadingSpinner />
                            ) : (
                              <HiOutlineTrash size={16} />
                            )}
                          </button>
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
};

export default UsersGrid;
